/* ============================================================
   DHAAGA TRIBE — Main JavaScript
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {

  // === SKIP LINK ===
  const skipLink = document.querySelector('.skip-to-content-link');
  if (skipLink) {
    skipLink.addEventListener('click', () => {
      const main = document.getElementById('MainContent');
      if (main) main.focus();
    });
  }

  // === PAGE LOADER ===
  const loader = document.getElementById('page-loader');
  if (loader) {
    window.addEventListener('load', () => {
      setTimeout(() => loader.classList.add('loaded'), 400);
    });
  }

  // === HEADER SCROLL EFFECT ===
  const header = document.getElementById('site-header');
  if (header) {
    let headerTicking = false;
    const onScroll = () => {
      if (!headerTicking) {
        requestAnimationFrame(() => {
          header.classList.toggle('scrolled', window.scrollY > 80);
          headerTicking = false;
        });
        headerTicking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // === SYNC HEADER HEIGHT CSS VARS TO REAL MEASUREMENTS ===
  // --header-height = full fixed chrome (announcement bar + header), used by
  // page content offsets. --subnav-header-offset = the header bar's own
  // height, used by the Collections megadrop: it's position:fixed but
  // .site-header has backdrop-filter, which makes it the megadrop's
  // containing block — so the megadrop's "top" needs to be measured from
  // the header's own box, not the viewport, or it lands with a stray gap.
  const siteChrome = document.getElementById('site-chrome');
  const siteHeaderEl = document.getElementById('site-header');
  if (siteChrome && siteHeaderEl) {
    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty('--header-height', siteChrome.offsetHeight + 'px');
      document.documentElement.style.setProperty('--subnav-header-offset', siteHeaderEl.offsetHeight + 'px');
    };
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);
    if (window.ResizeObserver) new ResizeObserver(syncHeaderHeight).observe(siteChrome);
  }

  // === MENU DRAWER ===
  const menuOpenBtn  = document.getElementById('menu-open-btn');
  const menuOverlay  = document.getElementById('menu-overlay');
  const menuCloseBtn = document.getElementById('menu-close');

  const openMenu = () => {
    menuOverlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    menuOpenBtn?.setAttribute('aria-expanded', 'true');
  };

  const closeMenu = () => {
    menuOverlay?.classList.remove('open');
    document.body.style.overflow = '';
    menuOpenBtn?.setAttribute('aria-expanded', 'false');
  };

  menuOpenBtn?.addEventListener('click', openMenu);
  menuCloseBtn?.addEventListener('click', closeMenu);
  menuOverlay?.addEventListener('click', (e) => { if (e.target === menuOverlay) closeMenu(); });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeMenu();
      closeCart();
      closeSearch();
      closeWishlist();
    }
  });

  // === CART DRAWER ===
  const cartOpenBtn = document.getElementById('cart-open-btn');

  const openCart = () => {
    const ov = document.getElementById('cart-overlay');
    ov?.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  const closeCart = () => {
    const ov = document.getElementById('cart-overlay');
    ov?.classList.remove('open');
    document.body.style.overflow = '';
  };

  // Delegated: close button, remove, qty — all survive drawer HTML refresh
  document.addEventListener('click', async (e) => {
    if (e.target.closest('#cart-close') || e.target.closest('#cart-continue')) { closeCart(); return; }
    const ov = document.getElementById('cart-overlay');
    if (ov && e.target === ov) { closeCart(); return; }

    // Quantity + / -
    const qBtn = e.target.closest('.cart-item__qb');
    if (qBtn) {
      const item = qBtn.closest('.cart-item');
      const key = item?.dataset.key;
      const qtyEl = item?.querySelector('.cart-item__qn');
      const cur = parseInt(qtyEl?.textContent || 1);
      const next = qBtn.dataset.action === 'increase' ? cur + 1 : Math.max(0, cur - 1);
      if (key) await window.updateCartLine(key, next);
      return;
    }

    // Remove
    const rmBtn = e.target.closest('.cart-item__rm');
    if (rmBtn) {
      e.preventDefault();
      const item = rmBtn.closest('.cart-item');
      const key = item?.dataset.key;
      if (key) await window.updateCartLine(key, 0);
    }
  });

  cartOpenBtn?.addEventListener('click', openCart);

  // === CART: refresh drawer via Section Rendering API ===
  async function refreshAndOpenCart() {
    // Always open the drawer first so the user sees something immediately
    openCart();
    try {
      const [sectRes, cartRes] = await Promise.all([
        fetch('/cart?sections=cart-drawer'),
        fetch('/cart.js')
      ]);
      if (sectRes.ok) {
        const sections = await sectRes.json();
        const html = sections['cart-drawer'];
        if (html) {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const newOv = tmp.querySelector('#cart-overlay');
          const oldOv = document.getElementById('cart-overlay');
          if (newOv && oldOv) oldOv.innerHTML = newOv.innerHTML;
        }
      }
      if (cartRes.ok) {
        const cart = await cartRes.json();
        const badge = document.getElementById('cart-count');
        if (badge) {
          badge.textContent = cart.item_count;
          badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      }
    } catch(e) { console.error('cart refresh error:', e); }
  }

  // === CART LINE UPDATE (qty change / remove) — uses item.key, not line index ===
  window.updateCartLine = async function(key, quantity) {
    const ov = document.getElementById('cart-overlay');
    if (ov) ov.style.pointerEvents = 'none';
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: parseInt(quantity) })
      });
      if (!res.ok) throw new Error('change failed');
      await refreshAndOpenCart();
    } catch(err) { console.error(err); }
    finally { if (ov) ov.style.pointerEvents = ''; }
  };

  // === TOAST NOTIFICATION ===
  function dtShowToast(title, size) {
    const old = document.getElementById('dt-toast');
    if (old) old.remove();
    const t = document.createElement('div');
    t.id = 'dt-toast';
    t.className = 'dt-toast';
    t.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>'
      + '<div class="dt-toast__body"><strong>Added to bag</strong>'
      + (title ? '<span>' + title + (size ? ' &nbsp;·&nbsp; Size ' + size : '') + '</span>' : '')
      + '</div>';
    document.body.appendChild(t);
    requestAnimationFrame(() => requestAnimationFrame(() => t.classList.add('dt-toast--in')));
    setTimeout(() => { t.classList.remove('dt-toast--in'); setTimeout(() => t.remove(), 350); }, 2800);
  }

  // === GLOBAL: add to cart ===
  window.dtAddToCart = async function(variantId, quantity, btn) {
    quantity = quantity || 1;
    const id = parseInt(variantId, 10);
    if (!id) { console.error('dtAddToCart: invalid variantId', variantId); return; }

    const origHTML = btn ? btn.innerHTML : '';
    if (btn) { btn.innerHTML = 'Adding…'; btn.disabled = true; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id, quantity })
      });

      if (!res.ok) {
        let msg = 'Could not add to bag.';
        try { const data = await res.json(); msg = data.description || data.message || msg; } catch(_) {}
        if (btn) { btn.innerHTML = msg; btn.disabled = false; setTimeout(() => { btn.innerHTML = origHTML; }, 2500); }
        console.error('cart/add.js error:', res.status, msg);
        return;
      }

      const added = await res.json().catch(() => null);

      // Success — show toast then refresh drawer
      const titleEl = btn
        ? (btn.dataset.productTitle || document.querySelector('.product-title, .pp__title')?.textContent?.trim() || '')
        : '';
      const sizeBtn = document.querySelector('.size-btn.active, [data-option-index].active');
      const sizeLabel = sizeBtn ? sizeBtn.textContent.trim() : '';
      dtShowToast(titleEl, sizeLabel);

      if (window.dtTrack && added) {
        window.dtTrack.addToCart({ id: added.id, name: added.product_title || titleEl, price: (added.price || 0) / 100, quantity: added.quantity || quantity });
      }

      await refreshAndOpenCart();
      if (btn) { btn.innerHTML = 'Added ✓'; setTimeout(() => { btn.innerHTML = origHTML; btn.disabled = false; }, 2000); }
    } catch(err) {
      console.error('dtAddToCart caught:', err);
      if (btn) { btn.innerHTML = 'Try again'; btn.disabled = false; setTimeout(() => { btn.innerHTML = origHTML; }, 2500); }
    }
  };

  // === FORM SUBMIT: size validation + AJAX cart add ===
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.action?.includes('/cart/add')) return;
    e.preventDefault();

    // Size validation: only if product has real size options and none explicitly selected
    const sizeGrid = form.querySelector('.size-grid');
    if (sizeGrid && !window.dtSizeSelected) {
      sizeGrid.classList.add('size-error');
      let msg = document.getElementById('dt-size-msg');
      if (!msg) {
        msg = document.createElement('p');
        msg.id = 'dt-size-msg';
        msg.className = 'dt-size-msg';
        msg.textContent = 'Please select a size to continue.';
        sizeGrid.after(msg);
      }
      msg.style.opacity = '1';
      sizeGrid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { sizeGrid.classList.remove('size-error'); msg.style.opacity = '0'; }, 2500);
      return;
    }

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput || !parseInt(variantInput.value, 10)) {
      console.error('ATC: no valid variant ID found', variantInput?.value);
      return;
    }
    const qtyInput = form.querySelector('[name="quantity"]');
    const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    await window.dtAddToCart(variantInput.value, quantity, form.querySelector('[type="submit"]'));
  });

  // === QUICK-ADD from collection page ===
  window.addToCartQuick = async function(btn) {
    const card = btn.closest('.pc');
    const selectedSz = card?.querySelector('.pc__sz.active');
    const fallbackSz = card?.querySelector('.pc__sz:not(.sold-out)');
    const variantId = selectedSz?.dataset.variantId || fallbackSz?.dataset.variantId || btn.dataset.variantId;
    if (!variantId) return;
    // If multiple sizes and none clicked, highlight size buttons
    const allSz = card?.querySelectorAll('.pc__sz');
    if (allSz?.length > 1 && !selectedSz) {
      card.querySelector('.pc__sizes')?.classList.add('size-error');
      setTimeout(() => card.querySelector('.pc__sizes')?.classList.remove('size-error'), 2000);
      return;
    }
    await window.dtAddToCart(variantId, 1, btn);
  };

  // === SEARCH OVERLAY ===
  const searchOpenBtn  = document.getElementById('search-open-btn');
  const searchOverlay  = document.getElementById('search-overlay');
  const searchCloseBtn = document.getElementById('search-close');
  const searchInput    = document.getElementById('search-input');
  const searchForm     = document.getElementById('search-form');
  const searchTrending = document.getElementById('search-trending');
  const searchLive     = document.getElementById('search-live');

  function searchReset() {
    if (searchInput) searchInput.value = '';
    if (searchLive) searchLive.innerHTML = '';
    if (searchTrending) searchTrending.style.display = '';
    if (searchInput) searchInput.setAttribute('aria-expanded', 'false');
  }

  const openSearch = () => {
    searchOverlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => searchInput?.focus(), 100);
  };

  const closeSearch = () => {
    searchOverlay?.classList.remove('open');
    document.body.style.overflow = '';
    searchReset();
  };

  searchOpenBtn?.addEventListener('click', openSearch);
  searchCloseBtn?.addEventListener('click', closeSearch);
  searchOverlay?.addEventListener('click', (e) => { if (e.target === searchOverlay) closeSearch(); });

  // === PREDICTIVE SEARCH ===
  // Shopify's built-in /search/suggest.json endpoint — no app or metafield
  // setup needed. Debounced so every keystroke doesn't fire a request.
  if (searchInput && searchLive) {
    var searchDebounce = null;
    var searchAbort = null;
    var searchActiveIndex = -1;

    function searchFormatMoney(cents) {
      return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
    }

    function searchItems() {
      return Array.prototype.slice.call(searchLive.querySelectorAll('.search-live__item'));
    }

    function searchSetActive(idx) {
      var items = searchItems();
      items.forEach(function(el) { el.classList.remove('active'); });
      if (idx >= 0 && idx < items.length) {
        items[idx].classList.add('active');
        items[idx].scrollIntoView({ block: 'nearest' });
      }
      searchActiveIndex = idx;
    }

    function renderSearchResults(query, products) {
      if (!products.length) {
        searchLive.innerHTML = '<p class="search-live__empty">No results for "' + query.replace(/</g, '&lt;') + '"</p>';
        return;
      }
      var html = products.map(function(p) {
        var img = p.image || (p.featured_image && p.featured_image.url) || '';
        if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        var priceCents = Math.round(parseFloat(p.price) * 100);
        return '<a href="' + p.url + '" class="search-live__item" role="option">' +
          '<span class="search-live__thumb">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="search-live__info">' +
            '<span class="search-live__name">' + p.title.replace(/</g, '&lt;') + '</span>' +
            '<span class="search-live__price">' + searchFormatMoney(priceCents) + '</span>' +
          '</span>' +
        '</a>';
      }).join('');
      html += '<a href="/search?q=' + encodeURIComponent(query) + '&type=product" class="search-live__viewall">View all results for "' + query.replace(/</g, '&lt;') + '" →</a>';
      searchLive.innerHTML = html;
    }

    searchInput.addEventListener('input', function() {
      var query = searchInput.value.trim();
      clearTimeout(searchDebounce);
      searchActiveIndex = -1;

      if (query.length < 2) {
        searchLive.innerHTML = '';
        if (searchTrending) searchTrending.style.display = '';
        searchInput.setAttribute('aria-expanded', 'false');
        return;
      }

      if (searchTrending) searchTrending.style.display = 'none';
      searchInput.setAttribute('aria-expanded', 'true');

      searchDebounce = setTimeout(function() {
        if (searchAbort) searchAbort.abort();
        searchAbort = new AbortController();
        fetch('/search/suggest.json?q=' + encodeURIComponent(query) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last',
          { signal: searchAbort.signal })
          .then(function(res) { return res.json(); })
          .then(function(data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            renderSearchResults(query, products);
          })
          .catch(function(err) { if (err.name !== 'AbortError') console.error('predictive search error:', err); });
      }, 250);
    });

    // Enter submits the form even when nothing is highlighted — belt-and-braces
    // fix since native Enter-to-submit wasn't reliably firing for this input.
    searchInput.addEventListener('keydown', function(e) {
      var items = searchItems();
      if (e.key === 'ArrowDown' && items.length) {
        e.preventDefault();
        searchSetActive(Math.min(searchActiveIndex + 1, items.length - 1));
      } else if (e.key === 'ArrowUp' && items.length) {
        e.preventDefault();
        searchSetActive(Math.max(searchActiveIndex - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (searchActiveIndex >= 0 && items[searchActiveIndex]) {
          window.location.href = items[searchActiveIndex].getAttribute('href');
        } else if (searchForm) {
          searchForm.requestSubmit ? searchForm.requestSubmit() : searchForm.submit();
        }
      }
    });
  }

  // === HERO SCROLL INDICATOR — fade out after scrolling past 30% of hero ===
  document.querySelectorAll('.hero__scroll').forEach(indicator => {
    const hero = indicator.closest('section') || indicator.parentElement;
    let threshold = hero.offsetHeight * 0.3;
    window.addEventListener('resize', () => { threshold = hero.offsetHeight * 0.3; }, { passive: true });

    let ticking = false;
    const apply = () => {
      const hide = window.scrollY > threshold;
      indicator.style.opacity = hide ? '0' : '';
      indicator.style.pointerEvents = hide ? 'none' : '';
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(apply);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  });

  // === SCROLL REVEAL ===
  const revealEls = document.querySelectorAll('.reveal');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view', 'in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    revealEls.forEach(el => observer.observe(el));
  }

  // === HERO VIDEO CONTROLS ===
  document.querySelectorAll('.hero').forEach(hero => {
    const video     = hero.querySelector('video');
    const muteBtn   = hero.querySelector('[data-hero-mute]');
    const playBtn   = hero.querySelector('[data-hero-play]');

    if (!video) return;

    muteBtn?.addEventListener('click', () => {
      video.muted = !video.muted;
      muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
      muteBtn.classList.toggle('unmuted', !video.muted);
    });

    playBtn?.addEventListener('click', () => {
      if (video.paused) {
        video.play();
        playBtn.setAttribute('aria-label', 'Pause');
        playBtn.classList.remove('paused');
      } else {
        video.pause();
        playBtn.setAttribute('aria-label', 'Play');
        playBtn.classList.add('paused');
      }
    });
  });

  // === PRODUCT GALLERY ===
  const gallery = document.querySelector('.product-gallery');
  if (gallery) {
    const mainImg = gallery.querySelector('.product-gallery__main img');
    const thumbs  = gallery.querySelectorAll('.product-gallery__thumb');

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        const src = thumb.querySelector('img')?.src;
        if (mainImg && src) {
          mainImg.style.opacity = '0';
          setTimeout(() => {
            mainImg.src = src;
            mainImg.style.opacity = '1';
          }, 200);
        }
      });
    });

    if (mainImg) {
      mainImg.style.transition = 'opacity 0.3s ease';
    }
  }

  // === PRODUCT VARIANT SELECTION ===
  document.querySelectorAll('.variant-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('sold-out')) return;
      const group = btn.closest('.product-info__variants');
      group?.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // === ACCORDION ===
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      const isOpen = item.classList.contains('open');
      document.querySelectorAll('.accordion-item').forEach(i => i.classList.remove('open'));
      if (!isOpen) item.classList.add('open');
    });
  });

  // === UPDATE CART COUNT ===
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      const countEl = document.getElementById('cart-count') || document.getElementById('cart-badge');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
    } catch (e) {}
  }

  // === GSAP ANIMATIONS (desktop only — parallax & scrub cause mobile scroll jank) ===
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  if (!isMobile && typeof gsap !== 'undefined') {
    // Register ScrollTrigger
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }

    // Hero entrance animation — set initial state via JS so CSS fallback stays visible
    const heroContent = document.querySelector('.hero__content');
    if (heroContent) {
      const els = [
        heroContent.querySelector('.hero__eyebrow'),
        heroContent.querySelector('.hero__title'),
        heroContent.querySelector('.hero__subtitle'),
        heroContent.querySelector('.hero__cta')
      ].filter(Boolean);
      gsap.set(els, { opacity: 0, y: 20 });
      const tl = gsap.timeline({ delay: 0.6 });
      tl.to(els[0], { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' })
        .to(els[1], { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }, '-=0.5')
        .to(els[2], { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.6')
        .to(els[3], { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.5');
    }

    // Parallax on hero media (desktop only — scrub: true fights mobile momentum scrolling)
    const heroMedia = document.querySelector('.hero__media');
    if (heroMedia && typeof ScrollTrigger !== 'undefined') {
      gsap.to(heroMedia, {
        yPercent: 20,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero',
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });
    }

  }

  // === MARQUEE DUPLICATION for seamless loop ===
  const marqueeInner = document.querySelector('.marquee-bar__inner, .marquee-inner');
  if (marqueeInner) {
    const clone = marqueeInner.cloneNode(true);
    marqueeInner.parentElement.appendChild(clone);
  }

  // === SUBNAV: hover line + Collections megadrop touch support ===
  document.querySelectorAll('.subnav-link').forEach(link => {
    const li = link.parentElement;
    link.addEventListener('mouseenter', () => li.classList.add('active'));
    link.addEventListener('mouseleave', () => li.classList.remove('active'));
    link.addEventListener('touchstart', () => li.classList.add('active'), { passive: true });
    link.addEventListener('touchend', () => setTimeout(() => li.classList.remove('active'), 350), { passive: true });
  });

  // Dropdown nav items with a real link trigger (Collections):
  // first tap opens, second tap navigates
  document.querySelectorAll('.subnav-item--has-drop').forEach((item) => {
    const link = item.querySelector('a.subnav-link--collections');
    if (!link) return;
    link.addEventListener('touchstart', (e) => {
      if (item.classList.contains('touch-open')) return; // second tap → follow link
      e.preventDefault();
      item.classList.add('touch-open');
      link.setAttribute('aria-expanded', 'true');
      const closeOnTap = (ev) => {
        if (!item.contains(ev.target)) {
          item.classList.remove('touch-open');
          link.setAttribute('aria-expanded', 'false');
          document.removeEventListener('touchstart', closeOnTap);
        }
      };
      setTimeout(() => document.addEventListener('touchstart', closeOnTap, { passive: true }), 0);
    }, { passive: false });
  });

  // Dropdown nav items with a button trigger (Shop by Category/Pattern,
  // Our Story, Customer Care) — no destination page, so click just toggles
  // the menu open/closed instead of navigating.
  document.querySelectorAll('.subnav-item--has-drop').forEach((item) => {
    const btn = item.querySelector('button.subnav-link--collections');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const isOpen = item.classList.toggle('touch-open');
      btn.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        const closeOnOutside = (ev) => {
          if (!item.contains(ev.target)) {
            item.classList.remove('touch-open');
            btn.setAttribute('aria-expanded', 'false');
            document.removeEventListener('click', closeOnOutside);
          }
        };
        setTimeout(() => document.addEventListener('click', closeOnOutside), 0);
      }
    });
  });

  // Desktop hover-intent with a short close delay. The CSS :hover rules
  // (plus the invisible bridge under the trigger) work for a clean vertical
  // path, but any diagonal or fast mouse movement from the trigger down into
  // the dropdown can momentarily leave both boxes and close it via pure
  // :hover before the pointer arrives. Toggling the same `touch-open` class
  // the click handler above already uses — with a grace period before
  // closing — tolerates any real mouse path instead of requiring a
  // pixel-perfect one.
  document.querySelectorAll('.subnav-item--has-drop').forEach((item) => {
    const trigger = item.querySelector('.subnav-link--collections');
    if (!trigger) return;
    let closeTimer = null;
    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      item.classList.add('touch-open');
      trigger.setAttribute('aria-expanded', 'true');
    });
    item.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => {
        item.classList.remove('touch-open');
        trigger.setAttribute('aria-expanded', 'false');
      }, 300);
    });
  });

  // Initialize cart count on load
  updateCartCount();

  // === WISHLIST — localStorage-backed, stores metadata, includes drawer ===
  const WL_KEY = 'dt_wishlist';

  function wlGet() {
    try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch { return []; }
  }
  function wlSet(items) {
    localStorage.setItem(WL_KEY, JSON.stringify(items));
  }
  function wlHas(id) {
    return wlGet().some(i => (typeof i === 'object' ? i.id : i) === String(id));
  }

  function updateWishlistCount() {
    const items = wlGet();
    const badge = document.getElementById('wishlist-count');
    if (badge) {
      badge.textContent = items.length;
      badge.style.display = items.length > 0 ? 'flex' : 'none';
    }
    const drawerCount = document.getElementById('wishlist-drawer-count');
    if (drawerCount) drawerCount.textContent = items.length ? '(' + items.length + ')' : '';
  }

  function renderWishlistDrawer() {
    const body = document.getElementById('wishlist-items');
    if (!body) return;
    const items = wlGet();
    if (!items.length) {
      body.innerHTML = '<div class="wishlist-empty">'
        + '<div class="wishlist-empty__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" width="36" height="36"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></div>'
        + '<p class="wishlist-empty__text">Your wishlist is empty</p>'
        + '<a href="/collections/all" class="wishlist-empty__link">Browse Collections</a>'
        + '</div>';
      return;
    }
    body.innerHTML = items.map(item => {
      const id    = typeof item === 'object' ? item.id    : item;
      const title = (typeof item === 'object' ? item.title : '') || 'Product';
      const image = (typeof item === 'object' ? item.image : '') || '';
      const url   = (typeof item === 'object' ? item.url   : '') || '/collections/all';
      return '<div class="wishlist-item" data-id="' + id + '">'
        + '<div class="wishlist-item__thumb"><a href="' + url + '">'
        + (image ? '<img src="' + image + '" alt="' + title + '" loading="lazy">' : '')
        + '</a></div>'
        + '<div class="wishlist-item__info">'
        + '<a href="' + url + '" class="wishlist-item__title">' + title + '</a>'
        + '<div class="wishlist-item__actions">'
        + '<a href="' + url + '" class="wishlist-item__view">View Product</a>'
        + '<button class="wishlist-item__remove" data-remove-id="' + id + '">Remove</button>'
        + '</div></div></div>';
    }).join('');
  }

  const openWishlist = () => {
    document.getElementById('wishlist-overlay')?.classList.add('open');
    document.body.style.overflow = 'hidden';
    renderWishlistDrawer();
    updateWishlistCount();
  };

  const closeWishlist = () => {
    document.getElementById('wishlist-overlay')?.classList.remove('open');
    document.body.style.overflow = '';
  };

  document.getElementById('header-wishlist-btn')?.addEventListener('click', openWishlist);

  // Delegated: drawer close + backdrop + remove items
  document.addEventListener('click', (e) => {
    if (e.target.closest('#wishlist-close')) { closeWishlist(); return; }
    const wlOv = document.getElementById('wishlist-overlay');
    if (wlOv && e.target === wlOv) { closeWishlist(); return; }
    const removeBtn = e.target.closest('[data-remove-id]');
    if (removeBtn) {
      const id = String(removeBtn.dataset.removeId);
      wlSet(wlGet().filter(i => (typeof i === 'object' ? i.id : i) !== id));
      document.querySelectorAll(`[data-product-id="${id}"]`).forEach(b => {
        b.classList.remove('wishlisted');
        const lbl = b.querySelector('.btn-wishlist__label');
        if (lbl) lbl.textContent = 'Save to Wishlist';
        b.setAttribute('aria-label', 'Save to wishlist');
      });
      updateWishlistCount();
      renderWishlistDrawer();
    }
  });

  function wlApplyAll() {
    const saved = wlGet();
    document.querySelectorAll('[data-product-id]').forEach(btn => {
      if (!btn.classList.contains('pc__wish') &&
          !btn.classList.contains('product-card__wishlist') &&
          !btn.classList.contains('btn-wishlist')) return;
      const id = String(btn.dataset.productId);
      const active = saved.some(i => (typeof i === 'object' ? i.id : i) === id);
      btn.classList.toggle('wishlisted', active);
      const label = btn.querySelector('.btn-wishlist__label');
      if (label) label.textContent = active ? 'Saved to Wishlist' : 'Save to Wishlist';
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
    });
  }

  window.dtToggleWishlist = function(btn) {
    const id = String(btn.dataset.productId);
    if (!id) return;
    const title = btn.dataset.productTitle || '';
    const image = btn.dataset.productImage || '';
    const url   = btn.dataset.productUrl   || '';
    let items = wlGet();
    const isNow = items.some(i => (typeof i === 'object' ? i.id : i) === id);
    if (isNow) {
      items = items.filter(i => (typeof i === 'object' ? i.id : i) !== id);
    } else {
      items.push({ id, title, image, url });
    }
    wlSet(items);
    document.querySelectorAll(`[data-product-id="${id}"]`).forEach(b => {
      b.classList.toggle('wishlisted', !isNow);
      const lbl = b.querySelector('.btn-wishlist__label');
      if (lbl) lbl.textContent = !isNow ? 'Saved to Wishlist' : 'Save to Wishlist';
      b.setAttribute('aria-label', !isNow ? 'Remove from wishlist' : 'Save to wishlist');
    });
    btn.classList.add('wish-pulse');
    setTimeout(() => btn.classList.remove('wish-pulse'), 300);
    updateWishlistCount();
  };

  wlApplyAll();
  updateWishlistCount();

  // === GSAP SCROLL PARALLAX ===
  // gsap.min.js + scrolltrigger.min.js are already loaded on every page (layout/theme.liquid)
  // but nothing used them — this is the first real use of that budget.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (window.gsap && window.ScrollTrigger && !prefersReducedMotion) {
    gsap.registerPlugin(ScrollTrigger);

    // Hero background drifts slightly slower than the scroll — the image is sized to
    // 112% height in CSS specifically to give this room without exposing an edge.
    const heroImg = document.querySelector('.hero__bg-img');
    if (heroImg) {
      gsap.to(heroImg, {
        yPercent: 8,
        ease: 'none',
        scrollTrigger: {
          trigger: heroImg.closest('.hero'),
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6
        }
      });
    }

    // Placement / craft-technique feature image — same idea, driven through a CSS
    // custom property (--py) so it composes with the existing CSS hover-scale instead
    // of GSAP's inline transform clobbering it.
    document.querySelectorAll('.placement-feature__media-inner').forEach((el) => {
      const state = { y: -5 };
      gsap.to(state, {
        y: 5,
        ease: 'none',
        onUpdate: () => el.style.setProperty('--py', state.y + '%'),
        scrollTrigger: {
          trigger: el.closest('.placement-feature') || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6
        }
      });
    });
  }
});
